import React, { useState, useMemo } from 'react';
import { ShoppingCart, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { cn } from '../../../lib/utils';

interface ShoppingItem {
  id: string;
  name: string;
  quantity: string;
  category: string;
  checked: boolean;
  sources: string[]; // Which recipes need this ingredient
}

interface Recipe {
  id: string;
  title: string;
  ingredients: string[];
}

interface ShoppingListGeneratorProps {
  recipes: Recipe[];
  onItemCheck?: (itemId: string, checked: boolean) => void;
  className?: string;
}

// Simple ingredient parsing - focuses on extracting item names
const parseIngredient = (ingredient: string) => {
  // Remove common quantity patterns and get the main ingredient
  const cleaned = ingredient
    .replace(/^\d+(\.\d+)?(\s)?(cups?|tbsp?|tsp?|oz|lbs?|pounds?|grams?|kg|ml|l)\s+/i, '')
    .replace(/^\d+(\.\d+)?\/\d+(\.\d+)?\s+/i, '') // fractions
    .replace(/^\d+(\.\d+)?\s+/i, '') // plain numbers
    .replace(/^(a|an)\s+/i, '')
    .toLowerCase()
    .trim();
  
  return cleaned || ingredient.toLowerCase();
};

// Categorize ingredients into store sections
const categorizeIngredient = (ingredient: string): string => {
  const lower = ingredient.toLowerCase();
  
  if (lower.includes('chicken') || lower.includes('beef') || lower.includes('pork') || 
      lower.includes('fish') || lower.includes('salmon') || lower.includes('turkey')) {
    return 'Meat & Fish';
  }
  if (lower.includes('milk') || lower.includes('cheese') || lower.includes('yogurt') || 
      lower.includes('butter') || lower.includes('cream') || lower.includes('eggs')) {
    return 'Dairy & Eggs';
  }
  if (lower.includes('apple') || lower.includes('banana') || lower.includes('orange') || 
      lower.includes('onion') || lower.includes('tomato') || lower.includes('carrot') ||
      lower.includes('lettuce') || lower.includes('potato') || lower.includes('pepper')) {
    return 'Produce';
  }
  if (lower.includes('bread') || lower.includes('pasta') || lower.includes('rice') || 
      lower.includes('flour') || lower.includes('cereal')) {
    return 'Bakery & Grains';
  }
  if (lower.includes('salt') || lower.includes('pepper') || lower.includes('oil') || 
      lower.includes('vinegar') || lower.includes('sauce') || lower.includes('spice')) {
    return 'Pantry & Condiments';
  }
  
  return 'Other';
};

const CATEGORY_ORDER = [
  'Produce',
  'Meat & Fish', 
  'Dairy & Eggs',
  'Bakery & Grains',
  'Pantry & Condiments',
  'Other'
];

export function ShoppingListGenerator({ 
  recipes, 
  onItemCheck,
  className 
}: ShoppingListGeneratorProps) {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  // Generate shopping list from recipes
  const shoppingList = useMemo(() => {
    const itemMap = new Map<string, ShoppingItem>();
    
    recipes.forEach((recipe) => {
      recipe.ingredients.forEach((ingredient, index) => {
        const itemName = parseIngredient(ingredient);
        const itemId = itemName;
        
        if (itemMap.has(itemId)) {
          // Add to existing item
          const existingItem = itemMap.get(itemId)!;
          existingItem.sources.push(recipe.title);
          // For now, just append quantities (simple approach)
          if (!existingItem.quantity.includes(ingredient)) {
            existingItem.quantity += `, ${ingredient}`;
          }
        } else {
          // Create new item
          itemMap.set(itemId, {
            id: itemId,
            name: itemName,
            quantity: ingredient,
            category: categorizeIngredient(itemName),
            checked: checkedItems.has(itemId),
            sources: [recipe.title]
          });
        }
      });
    });

    return Array.from(itemMap.values());
  }, [recipes, checkedItems]);

  // Group by category
  const categorizedItems = useMemo(() => {
    const categories = new Map<string, ShoppingItem[]>();
    
    shoppingList.forEach((item) => {
      const category = item.category;
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)!.push(item);
    });

    // Sort categories by predefined order
    const sortedCategories = CATEGORY_ORDER.map(categoryName => ({
      name: categoryName,
      items: categories.get(categoryName) || []
    })).filter(category => category.items.length > 0);

    // Add any remaining categories
    categories.forEach((items, categoryName) => {
      if (!CATEGORY_ORDER.includes(categoryName)) {
        sortedCategories.push({ name: categoryName, items });
      }
    });

    return sortedCategories;
  }, [shoppingList]);

  const handleItemCheck = (itemId: string) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(itemId)) {
      newChecked.delete(itemId);
    } else {
      newChecked.add(itemId);
    }
    setCheckedItems(newChecked);
    onItemCheck?.(itemId, newChecked.has(itemId));
  };

  const totalItems = shoppingList.length;
  const checkedCount = checkedItems.size;
  const progress = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;

  if (recipes.length === 0) {
    return (
      <Card className={cn('border-2 border-dashed border-gray-200', className)}>
        <CardContent className="p-8 text-center">
          <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-900 font-header mb-2">
            No Recipes Selected
          </h3>
          <p className="text-gray-600">
            Add some recipes to your meal plan to generate a shopping list.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-5 h-5 text-warning" />
              <span>Shopping List</span>
            </div>
            <Badge variant="outline" className="bg-coral/10 text-coral border-coral/20">
              {totalItems} items
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Progress: {checkedCount} of {totalItems} items
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(progress)}% complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCheckedItems(new Set())}
                disabled={checkedCount === 0}
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCheckedItems(new Set(shoppingList.map(item => item.id)))}
                disabled={checkedCount === totalItems}
              >
                <Check className="w-4 h-4 mr-1" />
                Check All
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shopping List by Category */}
      <div className="space-y-4">
        {categorizedItems.map((category) => (
          <ShoppingCategory
            key={category.name}
            categoryName={category.name}
            items={category.items}
            checkedItems={checkedItems}
            onItemCheck={handleItemCheck}
          />
        ))}
      </div>

      {/* Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xl font-semibold text-gray-900 font-header">
                {recipes.length}
              </div>
              <div className="text-sm text-gray-600">Recipes</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-900 font-header">
                {totalItems}
              </div>
              <div className="text-sm text-gray-600">Items</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-900 font-header">
                {categorizedItems.length}
              </div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-green-600 font-header">
                {checkedCount}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Separate component for each category section
function ShoppingCategory({ 
  categoryName, 
  items, 
  checkedItems, 
  onItemCheck 
}: {
  categoryName: string;
  items: ShoppingItem[];
  checkedItems: Set<string>;
  onItemCheck: (itemId: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const checkedInCategory = items.filter(item => checkedItems.has(item.id)).length;

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
            <CardTitle className="flex items-center justify-between text-lg">
              <div className="flex items-center space-x-2">
                <span>{categoryName}</span>
                <Badge variant="outline" className="text-xs">
                  {checkedInCategory}/{items.length}
                </Badge>
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    'flex items-start space-x-3 p-3 rounded-lg border transition-all',
                    checkedItems.has(item.id) 
                      ? 'bg-green-50 border-green-200 opacity-75' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  )}
                >
                  <button
                    onClick={() => onItemCheck(item.id)}
                    className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center mt-1 transition-colors',
                      checkedItems.has(item.id)
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    )}
                  >
                    {checkedItems.has(item.id) && <Check className="w-3 h-3" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      'font-medium text-gray-900',
                      checkedItems.has(item.id) && 'line-through text-gray-600'
                    )}>
                      {item.quantity}
                    </div>
                    {item.sources.length > 1 && (
                      <div className="text-sm text-gray-600 mt-1">
                        Used in: {item.sources.slice(0, 2).join(', ')}
                        {item.sources.length > 2 && ` +${item.sources.length - 2} more`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}